import * as React from 'react';

import { CategoryRecord } from '@rematter/modules/settings/_graphql/queryCategoriesList';
import { useTranslations } from '@rematter/modules/settings/i18n/useTranslations';
import { Badge, DataGridColumn, LocalizedDate } from '@rematter/ui-web';

type Props = {
  onRowClick?: (row: CategoryRecord) => void;
};

export const useCategoriesColumns = ({ onRowClick }: Props) => {
  const t = useTranslations();

  const handleCellDoubleClick = React.useCallback(
    ({ row }: { row: CategoryRecord }) => {
      onRowClick?.(row);
    },
    [onRowClick],
  );

  const columns: DataGridColumn<CategoryRecord>[] = React.useMemo(
    () => [
      {
        key: 'name',
        header: t.inventoryView.categoriesPanel.columns.name,
        sortable: true,
        width: 200,
        onDoubleClick: handleCellDoubleClick,
        cell: ({ row }) => (
          <div className="flex min-h-8 items-center text-left font-medium">
            {row.name}
          </div>
        ),
      },
      {
        key: 'description',
        header: t.inventoryView.categoriesPanel.columns.description,
        width: 300,
        onDoubleClick: handleCellDoubleClick,
        cell: ({ row }) => (
          <div className="truncate text-left text-neutral/70">
            {row.description || '-'}
          </div>
        ),
      },
      {
        key: 'attributes',
        header: t.inventoryView.categoriesPanel.columns.attributes,
        width: 120,
        onDoubleClick: handleCellDoubleClick,
        cell: ({ row }) => (
          <Badge color="neutral" size="small">
            {row.attributes.length}{' '}
            {row.attributes.length === 1
              ? t.inventoryView.categoriesPanel.badges.attribute
              : t.inventoryView.categoriesPanel.badges.attributes}
          </Badge>
        ),
      },
      {
        key: 'createdAt',
        header: t.inventoryView.categoriesPanel.columns.created,
        sortable: true,
        width: 120,
        onDoubleClick: handleCellDoubleClick,
        cell: ({ row }) => (
          <div className="text-left">
            <LocalizedDate date={row.timestamps.createdAt} />
          </div>
        ),
      },
    ],
    [t, handleCellDoubleClick],
  );

  const columnVisibilityState = React.useMemo(
    () => Object.fromEntries(columns.map((col) => [col.key, true])),
    [columns],
  );

  return { columns, columnVisibilityState };
};
